import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { HelmetProvider } from "react-helmet-async";
import PageLoader from "@/components/PageLoader";
import { usePageLoader } from "@/hooks/usePageLoader";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import { 
  SuspenseDashboard,
  SuspenseProfile, 
  SuspenseGallery,
  SuspenseGenerate,
  SuspenseECard,
  SuspenseResults
} from "@/components/LazyComponents";
import Privacy from "./pages/Privacy";
import About from "./pages/About";
import Terms from "./pages/Terms";
import HelpCenter from "./pages/HelpCenter";
import Themes from "./pages/Themes";
import PricingPage from "./pages/Pricing";
import API from "./pages/API";
import FamilyBondingColoring from "./pages/blog/FamilyBondingColoring";
import Status from "./pages/Status";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

const AppContent = () => {
  const { isLoading: pageLoading, showContent } = usePageLoader();
  const { loading: authLoading } = useAuth();
  
  const isLoading = pageLoading || authLoading;
  
  return (
    <>
      {isLoading && <PageLoader />}
      {showContent && !authLoading && (
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/dashboard" element={<SuspenseDashboard />} />
          <Route path="/profile" element={<SuspenseProfile />} />
          <Route path="/gallery" element={<SuspenseGallery />} />
          <Route path="/generate" element={<SuspenseGenerate />} />
          <Route path="/ecard" element={<SuspenseECard />} />
          <Route path="/results" element={<SuspenseResults />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/themes" element={<Themes />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/api" element={<API />} />
          <Route path="/status" element={<Status />} />
          <Route path="/blog/family-bonding-coloring" element={<FamilyBondingColoring />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      )}
    </>
  );
};

export default App;
